/*
  Einrasten — warum das kein Beiwerk ist.

  Kästen frei zu verschieben klingt nach Freiheit und endet ohne Hilfe in
  schiefen Folien: Niemand richtet Kanten mit dem Finger auf ein Prozent
  genau aus. Genau deshalb rasten PowerPoint und Google Slides ein, und
  genau deshalb sehen dort auch Folien von Ungeübten gerade aus.

  Eingerastet wird an drei Sorten Linien:

    die Folie selbst   Ränder und Mittelachsen
    die Nachbarn       deren Kanten und Mittelachsen
    ein grobes Raster  damit auch freie Stellen ordentlich bleiben

  Alles in Prozent der Folie — dieselbe Einheit wie die Kästen. Ein
  Schwellenwert in Pixeln würde je nach Bildschirmgröße mal früher, mal
  später fangen.
*/

/* Wie nah eine Kante sein muss, damit sie fängt. Prozent der Folie. */
const SCHWELLE = 1.2;

/* Das grobe Raster, wenn nichts anderes in der Nähe ist. */
const RASTER = 1;

/*
  Die Linien, an denen etwas fangen kann.

  `fremde` sind die anderen Kästen derselben Folie — der gezogene ist nicht
  dabei, sonst rastete er an sich selbst ein.
*/
export function linienFuer(fremde) {
  const senkrecht = [0, 50, 100];
  const waagerecht = [0, 50, 100];

  for (const k of fremde) {
    senkrecht.push(k.x, k.x + k.breite / 2, k.x + k.breite);
    waagerecht.push(k.y, k.y + k.hoehe / 2, k.y + k.hoehe);
  }

  return { senkrecht, waagerecht };
}

/*
  Einen Wert an die nächste Linie ziehen.

  Zurück kommt der eingerastete Wert und die Linie, an der er fing — die
  Oberfläche zeichnet daraus die Hilfslinie. Fängt nichts, kommt `null`
  zurück, und der Wert wird nur aufs Raster gerundet.
*/
function fange(wert, linien) {
  let beste = null;
  let abstand = SCHWELLE;

  for (const linie of linien) {
    const d = Math.abs(wert - linie);
    if (d < abstand) {
      abstand = d;
      beste = linie;
    }
  }

  if (beste === null) return { wert: Math.round(wert / RASTER) * RASTER, linie: null };
  return { wert: beste, linie: beste };
}

/*
  Welcher von zwei Treffern der bessere ist.

  ---------------------------------------------------------------
  Warum hier nicht einfach der kürzere Weg gewinnt
  ---------------------------------------------------------------

  Ein echter Fang an einer Kante und eine bloße Rundung aufs Raster sind
  nicht dasselbe, auch wenn beide einen Abstand haben. Verglich man nur die
  Abstände, gewänne regelmäßig die Rundung: Sie liegt nie weiter als ein
  halbes Raster entfernt, ein Fang dagegen darf bis zur Schwelle reichen.

  Genau das ist im Prüflauf passiert — ein Kasten, dessen Oberkante 0,6 %
  neben der Unterkante des Nachbarn stand, wurde auf 26 % gerundet statt auf
  25 % gefangen. Auf dem Bild sah es aus, als läge er bündig, und tat es
  doch nicht. Deshalb: Ein Fang schlägt jede Rundung, egal wie knapp.
*/
function besserAls(neu, alt) {
  if (!alt) return true;
  if (neu.gefangen !== alt.gefangen) return neu.gefangen;
  return neu.abstand < alt.abstand;
}

/*
  Einen Kasten beim Verschieben einrasten.

  Geprüft werden alle drei Kanten einer Richtung — linke, Mitte, rechte.
  Wer nur die linke prüfte, bekäme keine mittige Ausrichtung, und genau die
  braucht man für eine Überschrift.
*/
export function rasteVerschieben(kasten, fremde) {
  const { senkrecht, waagerecht } = linienFuer(fremde);

  const kandidatenX = [
    { versatz: 0, wert: kasten.x },
    { versatz: kasten.breite / 2, wert: kasten.x + kasten.breite / 2 },
    { versatz: kasten.breite, wert: kasten.x + kasten.breite },
  ];
  const kandidatenY = [
    { versatz: 0, wert: kasten.y },
    { versatz: kasten.hoehe / 2, wert: kasten.y + kasten.hoehe / 2 },
    { versatz: kasten.hoehe, wert: kasten.y + kasten.hoehe },
  ];

  const besteX = besterTreffer(kandidatenX, senkrecht);
  const besteY = besterTreffer(kandidatenY, waagerecht);

  return {
    x: begrenze(besteX.wert - besteX.versatz, 100 - kasten.breite),
    y: begrenze(besteY.wert - besteY.versatz, 100 - kasten.hoehe),
    hilfslinien: {
      senkrecht: besteX.linie,
      waagerecht: besteY.linie,
    },
  };
}

function besterTreffer(kandidaten, linien) {
  let beste = null;
  for (const k of kandidaten) {
    const treffer = fange(k.wert, linien);
    const naechster = {
      ...k,
      wert: treffer.wert,
      linie: treffer.linie,
      abstand: Math.abs(treffer.wert - k.wert),
      gefangen: treffer.linie !== null,
    };
    if (besserAls(naechster, beste)) beste = naechster;
  }
  return beste;
}

/*
  Beim Vergrößern rastet die bewegte Kante ein, die gegenüberliegende
  bleibt stehen. Alles andere wäre überraschend: Wer unten rechts zieht,
  erwartet, dass oben links nichts passiert.
*/
export function rasteGroesse(kasten, fremde, ecke) {
  const { senkrecht, waagerecht } = linienFuer(fremde);

  let { x, y, breite, hoehe } = kasten;

  let linieSenkrecht = null;
  let linieWaagerecht = null;

  if (ecke.includes("links")) {
    const rechts = x + breite;
    const gefangen = fange(x, senkrecht);
    x = gefangen.wert;
    breite = rechts - x;
    linieSenkrecht = gefangen.linie;
  } else {
    const gefangen = fange(x + breite, senkrecht);
    breite = gefangen.wert - x;
    linieSenkrecht = gefangen.linie;
  }

  if (ecke.includes("oben")) {
    const unten = y + hoehe;
    const gefangen = fange(y, waagerecht);
    y = gefangen.wert;
    hoehe = unten - y;
    linieWaagerecht = gefangen.linie;
  } else {
    const gefangen = fange(y + hoehe, waagerecht);
    hoehe = gefangen.wert - y;
    linieWaagerecht = gefangen.linie;
  }

  /*
    Ein Kasten, den man auf null zieht, wäre danach nicht mehr zu fassen —
    er hätte keine Fläche, die man antippen könnte. Fünf Prozent sind auf
    jedem Bildschirm noch groß genug für einen Finger.
  */
  const gewuenschteBreite = breite;
  const gewuenschteHoehe = hoehe;
  breite = Math.max(5, Math.min(breite, 100 - x));
  hoehe = Math.max(5, Math.min(hoehe, 100 - y));

  /*
    Musste die Mindestgröße eingreifen, sitzt die Kante nicht mehr auf der
    Linie — dann darf auch keine gezeigt werden. Eine Hilfslinie, an der
    nichts anliegt, wäre schlimmer als gar keine.
  */
  if (breite !== gewuenschteBreite) linieSenkrecht = null;
  if (hoehe !== gewuenschteHoehe) linieWaagerecht = null;

  return {
    x: Math.max(0, x),
    y: Math.max(0, y),
    breite,
    hoehe,
    hilfslinien: { senkrecht: linieSenkrecht, waagerecht: linieWaagerecht },
  };
}

function begrenze(wert, hoechstens) {
  return Math.max(0, Math.min(wert, hoechstens));
}
